import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type { AliasedIcon } from 'components/icon-registry';
import {
	LoButton,
	LoDialogService,
	LoEmptyState,
	LoSkeleton,
	LoToastService
} from 'lo-design-system';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AnaChannelConnectService } from '@brand-lolly/app/_services/ana-channel-connect.service';
import {
	UnlinkIntegrationDialogComponent,
	UnlinkIntegrationDialogData,
	UnlinkIntegrationDialogOutputData
} from '../components/unlink-integration-dialog.component';
import {
	ChatChannelCardComponent,
	ChatChannelCardModel
} from './chat-channel-card.component';
import {
	CHAT_CONNECT_RETURN_PATH,
	CHAT_RETURN_PARAMS,
	ChatChannelView,
	ChatChannelsService,
	ChatConnectReturn,
	ChatDeactivationReason,
	ChatPlatform
} from './chat-channels.service';
import { TeamsInstallDialogComponent } from './teams-install-dialog.component';

interface PlatformCopy {
	icon: AliasedIcon;
	name: string;
	description: string;
	/** Only Slack has an install we can start from here — see the Teams dialog. */
	hasConnectRoundTrip: boolean;
	/** What to call the workspace when the platform gave us no name for it. */
	unnamedWorkspace: string;
}

const PLATFORM_COPY: Record<ChatPlatform, PlatformCopy> = {
	slack: {
		icon: 'slack',
		name: 'Slack',
		description:
			'Ask Ana in a direct message, or @mention her in a channel she is in.',
		hasConnectRoundTrip: true,
		unnamedWorkspace: 'Your Slack workspace'
	},
	microsoft_teams: {
		icon: 'microsoft_teams',
		name: 'Microsoft Teams',
		description:
			'Ask Ana in a chat, or @mention her in a team channel she is in.',
		hasConnectRoundTrip: false,
		unnamedWorkspace: 'Your Microsoft Teams workspace'
	}
};

/**
 * Why a connection stopped, in words a brand can act on.
 *
 * The backend sends the stored vocabulary rather than a sentence, so the copy
 * lives here. Only the three reasons the brand did not choose ever arrive with a
 * `broken` state — their own revocation and a replacement are ordinary endings
 * and are reported as simply not connected.
 */
const PROBLEM_BY_REASON: Partial<Record<ChatDeactivationReason, string>> = {
	app_uninstalled:
		'Ana was removed from this workspace, so Lolly stopped delivering there.',
	destination_gone:
		'The conversation Ana posted in no longer exists, so Lolly stopped delivering there.',
	credential_revoked:
		'Access for Ana was withdrawn in this workspace, so Lolly stopped delivering there.'
};

const PROBLEM_FALLBACK =
	'This connection stopped working, so Lolly stopped delivering there.';

/**
 * Ana's chat workspaces, on the brand's Integrations screen.
 *
 * It sits here rather than in a page of its own because this is where a brand
 * already manages every other place Lolly reaches out from — mailboxes, Meta,
 * TikTok — and a chat workspace is the same kind of decision. The three states a
 * card can be in come from the server (`/api/chat/channels`); the copy for them
 * lives here, because the reason a binding ended is stored as a vocabulary and
 * has no English on the wire.
 */
@Component({
	standalone: true,
	selector: 'chat-channels-block',
	imports: [LoButton, LoEmptyState, LoSkeleton, ChatChannelCardComponent],
	template: `
		<div class="chat-block">
			<header class="page-header">
				<h2 class="lo-type-heading-3">Ana in chat</h2>
				<p class="lo-type-text-sm">
					Connect a chat workspace and Ana answers there, and sends
					what finishes while you are away.
				</p>
			</header>

			<div class="cards" [attr.aria-busy]="loading">
				@if (loading) { @for (_ of skeletonCards; track $index) {
				<lo-skeleton
					variant="block"
					height="calc(var(--lo-space-card) * 3)"
				></lo-skeleton>
				} } @if (!loading && loadFailed) {
				<lo-empty-state
					bordered
					heading="We could not load your chat connections"
				>
					<button
						lo-button
						variant="secondary"
						size="sm"
						type="button"
						(click)="load()"
					>
						Try again
					</button>
				</lo-empty-state>
				} @for (card of cards; track card.platform) {
				<chat-channel-card
					[model]="card"
					[busy]="busyPlatform === card.platform"
					(connect)="onConnect(card.platform)"
					(revoke)="onRevoke(card)"
					(instructions)="onInstructions()"
				></chat-channel-card>
				}
			</div>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;

				.page-header {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-xs);
					margin-bottom: var(--lo-space-gap-lg);

					p {
						color: var(--lo-color-text-secondary);
					}
				}

				.cards {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-lg);
				}
			}
		`
	]
})
export class ChatChannelsBlockComponent implements OnInit, OnDestroy {
	/** Starts TRUE: `ngOnInit` always fetches, and starting false makes the first
	 *  paint claim nothing is connected. */
	loading = true;
	loadFailed = false;

	cards: ChatChannelCardModel[] = [];

	/** The platform with a request in flight, so its card can disable itself
	 *  without freezing the other one. */
	busyPlatform: ChatPlatform | null = null;

	/** One placeholder per platform we know about, so the block does not change
	 *  height when the real cards land. */
	readonly skeletonCards = Object.keys(PLATFORM_COPY);

	private unsubscriber = new Subject<void>();

	constructor(
		private chatChannelsService: ChatChannelsService,
		private anaChannelConnect: AnaChannelConnectService,
		private dialogs: LoDialogService,
		private toasts: LoToastService,
		private router: Router,
		private activatedRoute: ActivatedRoute
	) {}

	ngOnInit(): void {
		// Read before the fetch, and from the snapshot: the query string is the
		// only record of what the round trip did, and it is cleared immediately
		// below so a refresh of the return URL cannot report the same install
		// twice.
		this.reportReturn(
			this.chatChannelsService.takeReturn(
				this.activatedRoute.snapshot.queryParamMap
			)
		);
		this.load();
	}

	load(): void {
		this.loading = true;
		this.loadFailed = false;
		this.chatChannelsService
			.list()
			.pipe(
				takeUntil(this.unsubscriber),
				finalize(() => (this.loading = false))
			)
			.subscribe({
				next: response => {
					this.cards = (response?.channels ?? [])
						.map(channel => this.toCard(channel))
						.filter(
							(card): card is ChatChannelCardModel =>
								card !== null
						);
				},
				error: () => {
					this.cards = [];
					this.loadFailed = true;
				}
			});
	}

	/**
	 * Hand the browser to Slack's consent screen.
	 *
	 * The path we come back to goes through `AnaChannelConnectService`, which
	 * appends its own marker when the assistant is open on a live conversation
	 * and returns the path untouched otherwise — so a brand who started this from
	 * a chat with Ana lands back in that thread, and one who started it here does
	 * not have a panel spring open at them.
	 */
	onConnect(platform: ChatPlatform): void {
		if (platform !== 'slack' || this.busyPlatform) {
			return;
		}
		this.busyPlatform = platform;

		const returnPath = this.anaChannelConnect.decorateReturnPath(
			CHAT_CONNECT_RETURN_PATH
		);

		this.chatChannelsService
			.startSlackInstall(returnPath)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: response => {
					if (!response?.authorization_url) {
						this.busyPlatform = null;
						this.toasts.error(
							'Slack did not give us a link to continue with. Please try again.',
							{ duration: 6000 }
						);
						return;
					}
					// Armed here and nowhere earlier: a start that fails must not
					// leave a ticket behind, or the next page load would report an
					// abandonment for a round trip that never began.
					this.chatChannelsService.armPendingInstall(platform);
					window.location.href = response.authorization_url;
				},
				error: error => {
					this.busyPlatform = null;
					this.toasts.error(
						error?.status === 503
							? 'Slack is not available on this environment yet.'
							: 'We could not start the Slack install. Please try again.',
						{ duration: 6000 }
					);
				}
			});
	}

	onRevoke(card: ChatChannelCardModel): void {
		if (this.busyPlatform) {
			return;
		}
		const data: UnlinkIntegrationDialogData = {
			accountName: card.workspaceLabel ?? card.name
		};

		this.dialogs
			.open<
				UnlinkIntegrationDialogComponent,
				UnlinkIntegrationDialogData,
				UnlinkIntegrationDialogOutputData
			>(UnlinkIntegrationDialogComponent, { data })
			.closed.pipe(takeUntil(this.unsubscriber))
			.subscribe(result => {
				if (result?.confirmed) {
					this.revoke(card);
				}
			});
	}

	onInstructions(): void {
		this.dialogs.open(TeamsInstallDialogComponent);
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	private revoke(card: ChatChannelCardModel): void {
		this.busyPlatform = card.platform;
		this.chatChannelsService
			.revoke(card.platform)
			.pipe(
				takeUntil(this.unsubscriber),
				finalize(() => (this.busyPlatform = null))
			)
			.subscribe({
				next: channel => {
					this.replaceCard(channel);
					this.toasts.success(`${card.name} disconnected`, {
						duration: 3000
					});
				},
				error: () => {
					this.toasts.error(
						`We could not disconnect ${card.name}. Please try again.`,
						{ duration: 6000 }
					);
				}
			});
	}

	/**
	 * Say what the round trip did, then take its parameters off the URL.
	 *
	 * `abandoned` is the case that would otherwise be silent: the brand pressed
	 * Cancel on the consent screen, or closed it and navigated back, and nothing
	 * on the return says so. Without the ticket the card would simply still read
	 * "Not connected" with no explanation of why the trip they just took changed
	 * nothing.
	 */
	private reportReturn(outcome: ChatConnectReturn | null): void {
		if (!outcome) {
			return;
		}
		const name = PLATFORM_COPY[outcome.platform]?.name ?? 'Chat';

		if (outcome.outcome === 'connected') {
			this.toasts.success(outcome.message ?? `${name} is connected.`, {
				duration: 8000
			});
		} else if (outcome.outcome === 'failed') {
			this.toasts.error(
				outcome.message ?? `${name} could not be connected.`,
				{ duration: 10000 }
			);
		} else {
			this.toasts.info(
				`${name} was not connected — the install was not finished. You can start it again here whenever you like.`,
				{ duration: 8000 }
			);
		}

		this.clearReturnParams();
	}

	/**
	 * Only our four, and only by merging: the mailbox and Meta callbacks land on
	 * this same route with parameters of their own, and a wholesale clear would
	 * swallow a return that is not ours.
	 */
	private clearReturnParams(): void {
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: {
				[CHAT_RETURN_PARAMS.status]: null,
				[CHAT_RETURN_PARAMS.platform]: null,
				[CHAT_RETURN_PARAMS.outcome]: null,
				[CHAT_RETURN_PARAMS.message]: null
			},
			queryParamsHandling: 'merge',
			replaceUrl: true
		});
	}

	private replaceCard(channel: ChatChannelView): void {
		const replacement = this.toCard(channel);
		if (!replacement) {
			return;
		}
		this.cards = this.cards.map(card =>
			card.platform === replacement.platform ? replacement : card
		);
	}

	/**
	 * Null for a platform this build has no copy for. The server owns the list of
	 * supported platforms, so a newer backend can name one this bundle predates —
	 * skipping it is better than a card with a blank icon and no name.
	 */
	private toCard(channel: ChatChannelView): ChatChannelCardModel | null {
		const copy = PLATFORM_COPY[channel.platform];
		if (!copy) {
			return null;
		}

		const bound = channel.state !== 'not_connected';
		const workspaceLabel = bound
			? channel.destination_label || copy.unnamedWorkspace
			: null;
		const problem =
			channel.state === 'broken'
				? (channel.deactivated_reason &&
						PROBLEM_BY_REASON[channel.deactivated_reason]) ||
				  PROBLEM_FALLBACK
				: null;

		return {
			platform: channel.platform,
			icon: copy.icon,
			name: copy.name,
			description: copy.description,
			state: channel.state,
			statusLabel: this.statusLabel(channel),
			workspaceLabel,
			workspaceReference: bound ? channel.destination_reference : null,
			problem,
			// Slack is offered whenever it is not currently live — including in
			// the broken state, where reconnecting is exactly the fix.
			canConnect:
				copy.hasConnectRoundTrip && channel.state !== 'connected',
			connectLabel: channel.state === 'broken' ? 'Reconnect' : 'Connect',
			canRevoke: channel.state === 'connected',
			// Teams has no round trip to start, so its card offers the steps
			// instead — before and after a connection, since a broken Teams
			// binding is fixed by adding the app again.
			canShowInstructions:
				!copy.hasConnectRoundTrip && channel.state !== 'connected',
			instructionsLabel:
				channel.state === 'broken' ? 'How to reconnect' : 'How to add',
			hasDetails: Boolean(workspaceLabel || problem)
		};
	}

	private statusLabel(channel: ChatChannelView): string {
		if (channel.state === 'connected') {
			return 'Connected';
		}
		return channel.state === 'broken' ? 'Not delivering' : 'Not connected';
	}
}
